import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Card,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import InputField from 'components/InputField';
import { FC } from 'react';
import strings from 'common/Translation/Translate';
import { Validators } from 'utils/validators';
import { formatCurrency } from 'utils/validators/HelperFunctions';

dayjs.extend(customParseFormat);

const SCHEDULE_TIME_FORMATS = ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A'] as const;

/** Parse start/end times stored as HH:mm strings or dayjs picker values. */
const parseScheduleTime = (value: unknown): Dayjs | null => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    for (const fmt of SCHEDULE_TIME_FORMATS) {
      const parsed = dayjs(trimmed, fmt, true);
      if (parsed.isValid()) return parsed;
    }
  }
  const parsed = dayjs(value as string | Date);
  return parsed.isValid() ? parsed : null;
};

export interface PricingStrategyValues {
  overridePricePerPerson: boolean;
  pricePerPerson: any;
  overrideMinimumRevenue: boolean;
  minimumRevenue: any;
  /** hallRental is the "Fixed Hall Rental" in the UI. */
  overrideHallRental: boolean;
  hallRental: any;
  overridePricePerHour?: boolean;
  pricePerHour?: any;
}

export interface PricingStrategyDefaults {
  pricePerPerson?: any;
  minimumRevenue?: any;
  hallRental?: any;
  pricePerHour?: any;
  /** Display name for the selected price package (e.g. "Wedding Package"). */
  packageName?: string;
  /** Display name for the selected venue (e.g. "Grand Ballroom"). */
  venueName?: string;
  /** e.g. "5.0 h × ₹2,000/h = ₹10,000" for total rental card + tooltip */
  hourlyRentalBreakdown?: string | null;
}

interface Props {
  values: PricingStrategyValues;
  defaults?: PricingStrategyDefaults;
  onChange: (next: PricingStrategyValues) => void;
  restrictPriceOverride?: boolean;
  hourlyPricing?: boolean;
}

const sanitizeNumber = (value: any) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
};

export const sanitizeDecimalNumber = (value: any, decimalPlaces = 3) => {
  if (value === '' || value === null || value === undefined) return '';

  const [integerPart, ...decimalParts] = String(value).replace(/[^\d.]/g, '').split('.');
  const decimalPart = decimalParts.join('').slice(0, decimalPlaces);

  return decimalParts.length > 0 ? `${integerPart}.${decimalPart}` : integerPart;
};

const formatDefaultAmount = (value: any): string | null => {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  if (typeof value === 'string' && /[₹$€£]/.test(value)) return value;
  const n = Number(value);
  if (value !== '' && Number.isFinite(n) && !Number.isNaN(n)) return formatCurrency(n);
  return null;
};

/** Total amount shown after "=" in the hourly breakdown line (e.g. "… = ₹1,799.89"). */
const parseTotalFromHourlyBreakdown = (breakdown: string | null | undefined): string | null => {
  if (!breakdown || typeof breakdown !== 'string') return null;
  const idx = breakdown.lastIndexOf('=');
  if (idx === -1) return null;
  const tail = breakdown.slice(idx + 1).trim();
  return tail.length > 0 ? tail : null;
};

/** Venue/package default rate, or overridden rate when the override switch is on. */
export function getEffectivePricePerHour(
  pricing: Pick<PricingStrategyValues, 'overridePricePerHour' | 'pricePerHour'>,
  venueDefaultRate?: unknown,
  fallbackRate?: unknown,
): number | null {
  if (pricing.overridePricePerHour && pricing.pricePerHour != null && pricing.pricePerHour !== '') {
    const overridden = Number(pricing.pricePerHour);
    return Number.isFinite(overridden) && overridden > 0 ? overridden : null;
  }
  const defaultRate = venueDefaultRate ?? fallbackRate;
  if (defaultRate == null || defaultRate === '') return null;
  const rate = Number(defaultRate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};

export function calcHourlyTotalRental(startTime: unknown, endTime: unknown, rate: unknown): number | '' {
  const hourlyRate = Number(rate);
  if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) return '';
  const start = parseScheduleTime(startTime);
  const end = parseScheduleTime(endTime);
  if (!start || !end) return '';
  const hours = end.diff(start, 'hours', true);
  if (!Number.isFinite(hours) || hours <= 0) return '';
  return hours * hourlyRate;
}

type AutoSource = 'package' | 'venue';

/** Optional: show under total rental when hourly; also fed into tooltip. */
export function buildHourlyRentalBreakdown(
  startTime: unknown,
  endTime: unknown,
  pricePerHour: unknown,
): string | undefined {
  if (pricePerHour === '' || pricePerHour == null) return undefined;
  const start = parseScheduleTime(startTime);
  const end = parseScheduleTime(endTime);
  if (!start || !end) return undefined;
  const hours = end.diff(start, 'hours', true);
  const rate = Number(pricePerHour);
  if (!Number.isFinite(hours) || hours <= 0 || !Number.isFinite(rate) || rate <= 0) return undefined;
  const total = hours * rate;
  return `${hours.toFixed(1)} h × ${formatCurrency(rate)}/h = ${formatCurrency(total)}`;
}

const statusChipBaseSx = {
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: 0.04,
  textTransform: 'uppercase' as const,
  '& .MuiChip-label': { px: 0.75, py: 0 },
};

const inheritedChipSx = {
  ...statusChipBaseSx,
  bgcolor: (theme: Theme) => alpha(theme.palette.text.secondary, 0.08),
  color: (theme: Theme) => theme.palette.text.secondary,
  border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.secondary, 0.22)}`,
};

const overriddenChipSx = {
  ...statusChipBaseSx,
  bgcolor: (theme: Theme) => alpha(theme.palette.info.main, 0.14),
  color: (theme: Theme) => theme.palette.info.dark,
  border: (theme: Theme) => `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
};

interface PricingFieldCardProps {
  title: string;
  infoTooltip: string;
  infoTooltipExtra?: string | null;
  /** When override is off, which auto source badge to show (if any). */
  autoSource?: AutoSource | null;
  override: boolean;
  /** Raw or formatted default amount shown in muted text when not overridden. */
  defaultAmount?: any;
  value: any;
  disabled?: boolean;
  allowOverride?: boolean;
  onOverrideChange: (override: boolean) => void;
  onValueChange: (value: string) => void;
  /** Amount suffix e.g. "/ person". */
  amountUnit?: string | null;
  /** Second line under amount (e.g. hours × rate = total). */
  subtitle?: string | null;
  /** Hide status chips (e.g. read-only total rental). */
  hideStatusChips?: boolean;
  allowDecimal?: boolean;
  sourceName?: string | null;
}

const buildSourceLine = (autoSource: AutoSource | null | undefined, sourceName?: string | null): string | null => {
  if (!autoSource) return null;
  const template = autoSource === 'package' ? strings.pricing_from_package : strings.pricing_from_venue;
  const fallback = autoSource === 'package' ? strings.new_booking_package : strings.new_booking_venue;
  return template.replace('{name}', sourceName || fallback);
};

const buildAmountWithUnit = (amount: string | null, unit?: string | null): string | null => {
  if (!amount) return null;
  return unit ? `${amount} ${unit}` : amount;
};

const buildOriginalLine = (
  formattedDefault: string | null,
  amountUnit?: string | null,
  autoSource?: AutoSource | null,
  sourceName?: string | null,
): string | null => {
  if (!formattedDefault) return null;
  const amount = buildAmountWithUnit(formattedDefault, amountUnit);
  if (!amount) return null;
  const name =
    sourceName || (autoSource === 'package' ? strings.new_booking_package : strings.new_booking_venue);
  if (autoSource && name) {
    return strings.pricing_original_with_source.replace('{amount}', amount).replace('{name}', name);
  }
  return strings.pricing_original_value.replace('{amount}', amount);
};

const PricingFieldCard: FC<PricingFieldCardProps> = ({
  title,
  infoTooltip,
  infoTooltipExtra,
  autoSource,
  override,
  defaultAmount,
  value,
  disabled,
  allowOverride = true,
  onOverrideChange,
  onValueChange,
  amountUnit,
  subtitle,
  hideStatusChips = false,
  allowDecimal = false,
  sourceName,
}) => {
  const formattedDefault = formatDefaultAmount(defaultAmount);
  const formattedOverride = override ? formatDefaultAmount(value) : null;
  const fromBreakdown = !override && subtitle ? parseTotalFromHourlyBreakdown(subtitle) : null;
  const amountLine = buildAmountWithUnit(
    override ? formattedOverride : formattedDefault ?? fromBreakdown,
    amountUnit,
  );
  const sourceLine = !override ? buildSourceLine(autoSource, sourceName) : null;
  const originalLine = override ? buildOriginalLine(formattedDefault, amountUnit, autoSource, sourceName) : null;
  const tooltipTitle = (
    <Box>
      <Typography variant="body2" sx={{ maxWidth: 280 }}>
        {infoTooltip}
      </Typography>
      {infoTooltipExtra ? (
        <Typography variant="caption" component="div" sx={{ mt: 0.75, display: 'block', opacity: 0.92 }}>
          {infoTooltipExtra}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, flex: 1 }}>
              {title}
            </Typography>
            <Tooltip title={tooltipTitle} placement="top" arrow enterTouchDelay={0}>
              <IconButton
                size="small"
                aria-label={strings.pricing_info_aria}
                sx={{ p: 0.25, mt: -0.25, color: 'text.disabled' }}
                tabIndex={-1}>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {amountLine && (
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ display: 'block', lineHeight: 1.4, mt: 0.25, fontWeight: 600 }}>
              {amountLine}
            </Typography>
          )}
          {sourceLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
              {sourceLine}
            </Typography>
          )}
          {originalLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
              {originalLine}
            </Typography>
          )}
          {subtitle && !sourceLine && !originalLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
              {subtitle}
            </Typography>
          )}

          {allowOverride && (
            <Box
              sx={{
                mt: 0.75,
                mb: override ? 0.5 : 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={override}
                    onChange={e => onOverrideChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    {override ? strings.pricing_override : strings.pricing_inherited}
                  </Typography>
                }
                sx={{ m: 0, flexShrink: 0, '& .MuiFormControlLabel-label': { lineHeight: 1 } }}
              />
              {!hideStatusChips ? (
                <Chip
                  size="small"
                  label={override ? strings.pricing_status_overridden : strings.pricing_status_inherited}
                  sx={override ? overriddenChipSx : inheritedChipSx}
                />
              ) : null}
            </Box>
          )}

          {allowOverride && override && (
            <InputField
              size="small"
              typeValue="number"
              disabled={disabled}
              inputValue={value ?? ''}
              inputProps={{
                inputMode: allowDecimal ? 'decimal' : 'numeric',
                pattern: allowDecimal ? '[0-9]*\\.?[0-9]{0,3}' : '[0-9]*',
              }}
              textChange={nextValue => onValueChange(nextValue)}
              validators={[
                { check: allowDecimal ? Validators.validateFloat : Validators.validateInt },
                { check: allowDecimal ? Validators.validateFloat : Validators.validateInt, max: 1000000000 },
              ]}
            />
          )}
        </Box>
      </Card>
    </Grid>
  );
};

/**
 * Override controls for booking line item pricing.
 *
 * Non-hourly: Price Per Person | Minimum Revenue | Fixed Hall Rental
 * Hourly:     Price Per Hour | Total Rental (read-only)
 */
const PricingStrategySection: FC<Props> = ({
  values,
  defaults = {},
  onChange,
  restrictPriceOverride = false,
  hourlyPricing = false,
}) => {
  const breakdown = defaults.hourlyRentalBreakdown ?? null;
  const packageName = defaults.packageName ?? null;
  const venueName = defaults.venueName ?? null;
  const totalRentalTooltipExtra =
    breakdown ||
    (defaults.hallRental != null && defaults.hallRental !== ''
      ? `${strings.pricing_info_total_rental_value_prefix} ${formatDefaultAmount(defaults.hallRental) ?? ''}`.trim()
      : null);

  if (hourlyPricing) {
    return (
      <Grid container spacing={1.5}>
        <PricingFieldCard
          title={strings.pricePerHour}
          infoTooltip={strings.pricing_info_price_per_hour}
          autoSource="venue"
          override={!!values.overridePricePerHour}
          defaultAmount={defaults.pricePerHour}
          value={values.pricePerHour}
          disabled={restrictPriceOverride}
          amountUnit={strings.pricing_unit_per_hour}
          sourceName={venueName}
          onOverrideChange={flag =>
            onChange({ ...values, overridePricePerHour: flag, pricePerHour: sanitizeNumber(values.pricePerHour) })
          }
          onValueChange={v => onChange({ ...values, overridePricePerHour: true, pricePerHour: sanitizeNumber(v) })}
        />
        <PricingFieldCard
          title={strings.new_booking_total_rental}
          infoTooltip={strings.pricing_info_total_rental}
          infoTooltipExtra={totalRentalTooltipExtra || undefined}
          override={false}
          defaultAmount={defaults.hallRental}
          value={values.hallRental}
          allowOverride={false}
          hideStatusChips
          subtitle={breakdown}
          onOverrideChange={() => {}}
          onValueChange={() => {}}
        />
      </Grid>
    );
  }

  return (
    <Grid container spacing={1.5}>
      <PricingFieldCard
        title={strings.bookingDetails_text_pricePerPerson}
        infoTooltip={strings.pricing_info_price_per_person}
        autoSource="package"
        override={!!values.overridePricePerPerson}
        defaultAmount={defaults.pricePerPerson}
        value={values.pricePerPerson}
        disabled={restrictPriceOverride}
        allowDecimal
        amountUnit={strings.pricing_unit_per_person}
        sourceName={packageName}
        onOverrideChange={flag =>
          onChange({
            ...values,
            overridePricePerPerson: flag,
            pricePerPerson: sanitizeDecimalNumber(values.pricePerPerson),
          })
        }
        onValueChange={v =>
          onChange({ ...values, overridePricePerPerson: true, pricePerPerson: sanitizeDecimalNumber(v) })
        }
      />
      <PricingFieldCard
        title={strings.minimum_revenue_text}
        infoTooltip={strings.pricing_info_minimum_revenue}
        autoSource="venue"
        override={!!values.overrideMinimumRevenue}
        defaultAmount={defaults.minimumRevenue}
        value={values.minimumRevenue}
        disabled={restrictPriceOverride}
        amountUnit={strings.pricing_unit_minimum_revenue}
        sourceName={venueName}
        onOverrideChange={flag =>
          onChange({ ...values, overrideMinimumRevenue: flag, minimumRevenue: sanitizeNumber(values.minimumRevenue) })
        }
        onValueChange={v => onChange({ ...values, overrideMinimumRevenue: true, minimumRevenue: sanitizeNumber(v) })}
      />
      <PricingFieldCard
        title={strings.pricing_field_fixed_hall_rental}
        infoTooltip={strings.pricing_info_fixed_hall_rental}
        autoSource="venue"
        override={!!values.overrideHallRental}
        defaultAmount={defaults.hallRental}
        value={values.hallRental}
        disabled={restrictPriceOverride}
        amountUnit={strings.pricing_unit_fixed_hall_rental}
        sourceName={venueName}
        onOverrideChange={flag =>
          onChange({ ...values, overrideHallRental: flag, hallRental: sanitizeNumber(values.hallRental) })
        }
        onValueChange={v => onChange({ ...values, overrideHallRental: true, hallRental: sanitizeNumber(v) })}
      />
    </Grid>
  );
};

export default PricingStrategySection;
